<template>
  <div>
    <a-badge :count="unreadCount" :dot="unreadCount > 0" :overflowCount="99">
      <a-dropdown
        :trigger="['click']"
        placement="bottomRight"
        overlayClassName="notification-dropdown"
        @openChange="handleDropdownVisible"
      >
        <a-button type="link" class="p-0">
          <span class="fa-solid fa-bell fs-5"></span>
        </a-button>
        <template #overlay>
          <div class="card border-0 shadow notification-panel">
            <div class="card-header bg-white py-3 px-4">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <strong class="fs-5">Thông báo</strong>
                <div class="d-flex align-items-center gap-3">
                  <div class="d-flex align-items-center">
                    <span class="me-2 small">Thông báo</span>
                    <a-switch
                      v-model:checked="notificationsEnabled"
                      @change="toggleNotifications"
                      :class="notificationsEnabled ? 'switch-active' : ''"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="notifications.length > 0"
                class="d-flex justify-content-between small mt-1"
              >
                <button
                  class="btn btn-link btn-sm p-0 text-decoration-none small"
                  @click.stop="viewAllNotifications()"
                >
                  <i class="fas fa-list me-1"></i>
                  Xem tất cả thông báo
                </button>
                <button
                  class="btn btn-link btn-sm p-0 text-decoration-none small"
                  @click.stop="markAllAsRead()"
                >
                  <i class="fas fa-check-double me-1"></i>
                  Đánh dấu đã đọc
                </button>
              </div>
            </div>

            <div
              v-if="notifications.length === 0"
              class="card-body text-center py-4"
            >
              <i class="fa-solid fa-bell-slash text-muted fs-4 mb-2"></i>
              <p class="text-muted mb-0">Không có thông báo mới</p>
            </div>

            <div
              v-else
              class="card-body p-0"
              style="max-height: 450px; overflow-y: auto"
              ref="notificationListRef"
            >
              <div
                v-for="(notification, index) in notifications"
                :key="notification.documentId || index"
                class="notification-item p-3"
                :class="{ unread: !notification.is_read }"
                @click="handleNotificationClick(notification)"
              >
                <div class="d-flex">
                  <div class="notification-icon me-2">
                    <i
                      :class="getNotificationIcon(notification.type || 'info')"
                    ></i>
                  </div>
                  <div class="notification-content">
                    <div class="fw-bold text-primary">
                      {{ notification.title }}
                    </div>
                    <div class="text-dark">{{ notification.body }}</div>
                    <div class="text-muted small mt-1">
                      {{
                        formatTime(
                          notification.createdAt
                        )
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="loading"
                class="d-flex justify-content-center align-items-center py-3"
              >
                <a-spin size="small" />
                <span class="ms-2">Đang tải...</span>
              </div>

              <div
                v-if="
                  !hasMoreNotifications && notifications.length > 0 && !loading
                "
                class="text-center py-2"
              >
                <span class="text-muted small"
                  >Không còn thông báo nào nữa</span
                >
              </div>
            </div>
          </div>
        </template>
      </a-dropdown>
    </a-badge>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import {
  getFCMToken,
  requestNotificationPermission,
  messaging
} from "@/firebase/firebase";
import {
  toggleNotificationFromServer,
  getNotificationsFromServer,
  markNotificationAsReadFromServer,
  markAllNotificationsAsReadFromServer,
  countUnread,
} from "@/api/strapi/notification";
import { subscribeFCMToServer } from "@/api/strapi/device-token";
import { useFollowStore } from "@/stores/follow";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import { Empty } from "ant-design-vue";
import { useRouter } from "vue-router";

dayjs.extend(relativeTime);
dayjs.locale("vi");

export default defineComponent({
  name: "NotificationList",
  setup() {
    const router = useRouter();
    const notifications = ref([]);
    const unreadCount = ref(0);
    const userKey = ref("user");
    const userFromLocalStorage = JSON.parse(
      localStorage.getItem(userKey.value)
    );
    const notificationsEnabled = ref(
      userFromLocalStorage?.user?.notification_enabled
    );
    const loading = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const hasMoreNotifications = ref(true);
    const notificationListRef = ref(null);
    const countInterval = ref(null);
    let unsubscribeFCM = null; // Lưu trữ hàm hủy đăng ký sự kiện FCM

    // Lấy số lượng thông báo chưa đọc
    const fetchUnreadCount = async () => {
      try {
        const response = await countUnread();
        if (response && response.count !== undefined) {
          unreadCount.value = response.count;
        }
      } catch (error) {
        console.error("Lỗi khi lấy số thông báo chưa đọc:", error);
      }
    };

    // Lưu ý: Hàm lắng nghe thông báo đã được bỏ qua
    // vì thông báo sẽ được xử lý qua Service Worker
    
    // Khởi tạo follow store
    const followStore = useFollowStore();

    const setupNotificationListener = () => {
      
    }

    // Lấy thông báo từ server
    const fetchNotifications = async (page = 1, isLoadMore = false) => {
      try {
        loading.value = true;

        // Lấy thông báo và danh sách giải đã theo dõi song song
        const [notificationsResponse] = await Promise.all([
          getNotificationsFromServer(page, 5),
          followStore.loadFollowedTournaments(),
        ]);

        if (notificationsResponse && notificationsResponse.items) {
          if (isLoadMore) {
            notifications.value = [
              ...notifications.value,
              ...notificationsResponse.items,
            ];
          } else {
            notifications.value = notificationsResponse.items;
          }

          currentPage.value = page;
          totalPages.value = notificationsResponse.totalPages || 1;
          hasMoreNotifications.value =
            page < (notificationsResponse.totalPages || 1);
        }
      } catch (error) {
        console.error("Lỗi khi tải thông báo:", error);
      } finally {
        loading.value = false;
      }
    };

     // Hàm bật tắt thông báo
     const toggleNotifications = async (checked) => {
      try {
        // Kiểm tra xem trình duyệt có hỗ trợ thông báo không
        if (!('Notification' in window)) {
          console.log('Trình duyệt không hỗ trợ thông báo');
          notificationsEnabled.value = false;
          alert('Trình duyệt của bạn không hỗ trợ thông báo');
          return;
        }
        
        // Lấy trạng thái quyền thông báo hiện tại trước khi làm bất cứ điều gì
        const permissionStatus = Notification.permission;
        console.log('Trạng thái quyền thông báo hiện tại:', permissionStatus);
        
        // Nếu cố gắng bật thông báo
        if (checked) {
          // Phải kiểm tra quyền trước khi cập nhật các giá trị
          
          // Trường hợp 1: Chưa quyết định (default)
          if (permissionStatus === 'default') {
            console.log('Người dùng chưa quyết định về quyền thông báo, hiển thị hộp thoại xin cấp quyền');
            
            // Yêu cầu quyền thông báo
            const token = await requestNotificationPermission();
            
            if (!token) {
              // Người dùng từ chối cấp quyền
              console.log('Người dùng từ chối cấp quyền thông báo');
              notificationsEnabled.value = false;
              
              // Cập nhật localStorage
              localStorage.setItem(userKey.value, JSON.stringify({
                ...userFromLocalStorage,
                user: {
                  ...userFromLocalStorage.user,
                  notification_enabled: false
                }
              }));
              
              // Cập nhật server
              await toggleNotificationFromServer(false);
              
              alert('Bạn cần cấp quyền thông báo để sử dụng tính năng này');
              return;
            }
            
            console.log('Người dùng đã cấp quyền thông báo, token:', token);
            
            // Đăng ký FCM token với server
            await subscribeFCMToServer(token);
            
            // Lưu trạng thái bật thông báo
            notificationsEnabled.value = true;
            localStorage.setItem(userKey.value, JSON.stringify({
              ...userFromLocalStorage,
              user: {
                ...userFromLocalStorage.user,
                notification_enabled: true
              }
            }));
            
            // Cập nhật trạng thái trên server
            await toggleNotificationFromServer(true);
            
            // Khởi tạo lắng nghe thông báo
            setupNotificationListener();
          } 
          // Trường hợp 2: Đã từ chối (denied)
          else if (permissionStatus === 'denied') {
            console.log('Người dùng đã từ chối thông báo trước đó');
            // Đặt trạng thái về false
            notificationsEnabled.value = false;
            
            // Cập nhật localStorage
            localStorage.setItem(userKey.value, JSON.stringify({
              ...userFromLocalStorage,
              user: {
                ...userFromLocalStorage.user,
                notification_enabled: false
              }
            }));
            
            // Cập nhật trạng thái trên server
            await toggleNotificationFromServer(false);
            
            alert('Bạn đã chặn quyền thông báo. Vui lòng vào cài đặt trình duyệt để cấp quyền.');
            return;
          } 
          // Trường hợp 3: Đã cấp quyền (granted)
          else if (permissionStatus === 'granted') {
            console.log('Người dùng đã cấp quyền thông báo trước đó');
            // Lấy token
            const token = await getFCMToken();
            
            if (!token) {
              console.log('Không lấy được FCM token');
              notificationsEnabled.value = false;
              
              // Cập nhật localStorage
              localStorage.setItem(userKey.value, JSON.stringify({
                ...userFromLocalStorage,
                user: {
                  ...userFromLocalStorage.user,
                  notification_enabled: false
                }
              }));
              
              // Cập nhật trạng thái trên server
              await toggleNotificationFromServer(false);
              
              alert('Không thể kết nối dịch vụ thông báo. Vui lòng thử lại sau.');
              return;
            }
            
            console.log('Lấy FCM token thành công:', token);
            
            // Đăng ký FCM token với server
            await subscribeFCMToServer(token);
            
            // Lưu trạng thái bật thông báo
            notificationsEnabled.value = true;
            localStorage.setItem(userKey.value, JSON.stringify({
              ...userFromLocalStorage,
              user: {
                ...userFromLocalStorage.user,
                notification_enabled: true
              }
            }));
            
            // Cập nhật trạng thái trên server
            await toggleNotificationFromServer(true);
            
            // Khởi tạo lắng nghe thông báo
            setupNotificationListener();
          }
        } else {
          // Tắt thông báo - không cần kiểm tra quyền
          console.log('Người dùng muốn tắt thông báo');
          
          // Đặt trạng thái về false
          notificationsEnabled.value = false;
          localStorage.setItem(userKey.value, JSON.stringify({
            ...userFromLocalStorage,
            user: {
              ...userFromLocalStorage.user,
              notification_enabled: false
            }
          }));
          
          // Cập nhật trạng thái trên server
          await toggleNotificationFromServer(false);
        }
      } catch (error) {
        console.error('Lỗi khi thay đổi trạng thái thông báo:', error);
        // Đặt trạng thái về giá trị an toàn
        notificationsEnabled.value = false;
        localStorage.setItem(userKey.value, JSON.stringify({
          ...userFromLocalStorage,
          user: {
            ...userFromLocalStorage.user,
            notification_enabled: false
          }
        }));
        
        // Thông báo lỗi cho người dùng
        alert('Có lỗi xảy ra khi thay đổi trạng thái thông báo. Vui lòng thử lại sau.');
      }
    };

    const markAllAsRead = async () => {
      await markAllNotificationsAsReadFromServer();
      notifications.value.forEach((notification) => {
        notification.is_read = true;
      });
      unreadCount.value = 0;
    };

    const markAsRead = async (notification) => {
      // Giữ nguyên nội dung hàm này
    };

    const loadMoreNotifications = async () => {
      if (!loading.value && hasMoreNotifications.value) {
        await fetchNotifications(currentPage.value + 1, true);
      }
    };

    const handleDropdownVisible = (visible) => {
      if (visible) {
        fetchNotifications(1, false);
        fetchUnreadCount();
      }
    };

    const handleScroll = (event) => {
      const element = event.target;
      if (
        element.scrollHeight - element.scrollTop <=
        element.clientHeight + 50
      ) {
        loadMoreNotifications();
      }
    };

    const formatTime = (time) => {
      return dayjs(time).fromNow();
    };

    const getNotificationIcon = (type) => {
      switch (type) {
        case "success":
          return "fas fa-check-circle text-success";
        case "error":
          return "fas fa-exclamation-circle text-danger";
        case "warning":
          return "fas fa-exclamation-triangle text-warning";
        case "info":
        default:
          return "fas fa-info-circle text-info";
      }
    };

    const handleNotificationClick = async (notification) => {
      // Đánh dấu đã đọc nếu chưa đọc
      if (!notification.is_read) {
        try {
          await markNotificationAsReadFromServer(notification.documentId);
          notification.is_read = true;
          if (unreadCount.value > 0) {
            unreadCount.value--;
          }
        } catch (error) {
          console.error("Lỗi khi đánh dấu thông báo đã đọc:", error);
        }
      }

      // Xử lý chuyển hướng dựa trên thông báo
      if (notification.target_path) {
        // Nếu có section cần cuộn đến
        if (notification.target_section) {
          // Lưu section cần cuộn đến trước khi chuyển trang
          sessionStorage.setItem(
            "scrollToSection",
            notification.target_section
          );
        }

        // Chuyển đến trang đích
        router.push(notification.target_path).then(() => {
          // Nếu có section cần cuộn đến, thực hiện sau khi trang đã tải xong
          if (notification.target_section) {
            // Sử dụng setTimeout để đảm bảo DOM đã được render
            setTimeout(() => {
              const element = document.getElementById(
                notification.target_section
              );
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
                // Xóa section đã lưu sau khi đã cuộn
                sessionStorage.removeItem("scrollToSection");
              }
            }, 100);
          }
        });
      } else if (notification.data && notification.data.url) {
        // Fallback cho trường hợp cũ sử dụng data.url
        window.location.href = notification.data.url;
      }
    };

    // Khởi tạo
    onMounted(async () => {
      try {
        // Kiểm tra quyền thông báo hiện tại
        const currentPermission = Notification.permission;

        // Nếu thông báo được bật trong localStorage nhưng chưa cấp quyền trên trình duyệt
        if (
          notificationsEnabled.value &&
          (currentPermission === "denied" || currentPermission === "default")
        ) {
          console.log(
            "Thông báo được bật trong cấu hình nhưng chưa cấp quyền trên trình duyệt"
          );

          // Tắt thông báo ở cả client và server
          notificationsEnabled.value = false;

          // Cập nhật localStorage
          if (userFromLocalStorage) {
            userFromLocalStorage.user.notification_enabled = false;
            localStorage.setItem(
              userKey.value,
              JSON.stringify(userFromLocalStorage)
            );
          }

          // Gọi API tắt thông báo ở server
          await toggleNotificationFromServer(false);
          console.log("Tắt thông báo trên server do chưa cấp quyền");
        }

        // Lấy thông báo từ server
        await fetchNotifications(1, false);

        // Lấy số lượng thông báo chưa đọc
        await fetchUnreadCount();

        // Thiết lập interval để kiểm tra thông báo mới mỗi phút
        countInterval.value = setInterval(() => {
          fetchUnreadCount();
        }, 7000);

        // Nếu thông báo đang bật, chỉ cần kiểm tra thông báo định kỳ
        if (notificationsEnabled.value) {
          const token = await getFCMToken();
          await subscribeFCMToServer(token)
          console.log('Thông báo đang được bật, sẽ tự động cập nhật');
        }

        // Kiểm tra nếu có section cần cuộn đến từ sessionStorage
        const sectionToScroll = sessionStorage.getItem("scrollToSection");
        if (sectionToScroll) {
          // Sử dụng setTimeout để đảm bảo DOM đã được render
          setTimeout(() => {
            const element = document.getElementById(sectionToScroll);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
            // Xóa section đã lưu sau khi đã cuộn
            sessionStorage.removeItem("scrollToSection");
          }, 100);
        }
      } catch (error) {
        console.error("Lỗi khi khởi tạo thông báo:", error);
      }
    });

    // Dọn dẹp khi component bị hủy
    onUnmounted(() => {
      if (countInterval.value) {
        clearInterval(countInterval.value);
      }
    });

    // Hàm chuyển hướng đến trang tất cả thông báo
    const viewAllNotifications = () => {
      router.push('/user/notification/all-notifications');
    };
    
    return {
      notifications,
      unreadCount,
      notificationsEnabled,
      loading,
      hasMoreNotifications,
      notificationListRef,
      toggleNotifications,
      markAllAsRead,
      handleNotificationClick,
      formatTime,
      getNotificationIcon,
      handleScroll,
      handleDropdownVisible,
      viewAllNotifications
    };
  },
});
</script>

<style scoped>
.notification-panel {
  width: 450px;
  max-width: 90vw;
  border-radius: 8px;
}

.notification-item {
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #f9f9f9;
}

.notification-item.unread {
  background-color: #f0f7ff;
}

.notification-icon {
  font-size: 1.2rem;
  margin-top: 2px;
}

.notification-content {
  flex: 1;
}

.switch-active :deep(.ant-switch-inner) {
  background-color: #1890ff;
}
</style>
